import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@apollo/client/react";
import { HeroComboBox } from "@app/components/hero-combobox";
import { formatOneworld } from "@app/components/oneworld-word";
import { PlacesAutocompleteInput } from "@app/components/places-autocomplete-input";
import { destinationOptions } from "@app/data/airport-options";
import {
  EVENT_CONTRACT_CODE,
  GET_LIVE_CONTRACT,
} from "@app/graphql/events.queries";
import { AuthModal } from "@app/components/templates/auth-modal";
import "./home-page.css";
import {
  MINIMUM_TRAVELER_COUNT,
  type PendingEventFormValues,
  loadPendingEvent,
  savePendingEvent,
} from "@app/lib/pending-event-storage";
import { MaterialCalendarIcon } from "@app/lib/events-admin-icons";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { useDateFormatter } from "react-aria";
import {
  Button as AriaButton,
  DateRangePicker as AriaDateRangePicker,
  type DateValue,
  Group as AriaGroup,
  Label as AriaLabel,
  OverlayTriggerStateContext,
  Popover as AriaPopover,
  Dialog as AriaPopoverDialog,
} from "react-aria-components";
import { Controller, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  Button,
  InputBase,
  RangeCalendar,
  TextAreaBase,
  ValidatedForm,
  createFormSchema,
  cx,
  sanitizeText,
  schemas,
  useValidatedForm,
} from "@app/lib/ui-foundation";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import { useEventNameAvailability } from "@app/hooks/use-event-name-availability";
import { useOrganizationId } from "@app/hooks/use-organization-id";

const DESCRIPTION_MAX_LENGTH = 1000;
const MINIMUM_EVENT_LEAD_DAYS = 60;
const BUILD_VERSION_QUERY = import.meta.env.VITE_BUILD_VERSION
  ? `?v=${encodeURIComponent(import.meta.env.VITE_BUILD_VERSION)}`
  : "";
const HOME_HERO_IMAGE_SRC = `${import.meta.env.BASE_URL}images/events-home-hero.webp${BUILD_VERSION_QUERY}`;
const HOME_HERO_FALLBACK_IMAGE_SRC = `${import.meta.env.BASE_URL}images/events-home-hero.jpg${BUILD_VERSION_QUERY}`;

const groupTypes = [
  "CORPORATE",
  "ORGANIC",
  "ONE_WORLD",
  "TRIP_LINKED",
  "RFP_DRIVEN",
] as const;

function getMinimumEventDate() {
  return today(getLocalTimeZone()).add({ days: MINIMUM_EVENT_LEAD_DAYS });
}

export const createEventSchema = createFormSchema({
  eventName: schemas.sanitizedRequiredString,
  eventType: schemas.enum(groupTypes, "Select an event type"),
  organizerName: z.string().optional().default(""),
  organizerEmail: z.string().optional().default(""),
  venueName: schemas.sanitizedRequiredString,
  venueAddress: schemas.sanitizedRequiredString,
  description: z
    .string()
    .trim()
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`,
    )
    .transform((value) => sanitizeText(value)),
  destinationLabel: schemas.sanitizedRequiredString,
  primaryAirportCode: schemas.sanitizedRequiredString,
  departDate: schemas.dateString.refine(
    (value) => value >= getMinimumEventDate().toString(),
    {
      message: `Depart date must be at least ${MINIMUM_EVENT_LEAD_DAYS} days from today`,
    },
  ),
  returnDate: schemas.dateString.refine(
    (value) => value >= getMinimumEventDate().toString(),
    {
      message: `Return date must be at least ${MINIMUM_EVENT_LEAD_DAYS} days from today`,
    },
  ),
  passengers: z
    .string()
    .trim()
    .min(1, "This field is required")
    .refine((value) => /^\d+$/.test(value), {
      message: "Enter a valid number of travelers",
    })
    .refine((value) => Number(value) >= MINIMUM_TRAVELER_COUNT, {
      message: `Enter at least ${MINIMUM_TRAVELER_COUNT} travelers`,
    }),
}).refine((data) => data.returnDate >= data.departDate, {
  message: "Return date must be on or after depart date",
  path: ["returnDate"],
});

type CreateEventFormValues = PendingEventFormValues;

function LegendField({
  label,
  controlId,
  required = false,
  children,
  className,
}: {
  label: string;
  controlId: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("events-legend-field", className)}>
      <label
        id={`${controlId}-label`}
        htmlFor={controlId}
        className="events-legend-field__label"
      >
        {label}
        {required ? <span aria-hidden="true">*</span> : null}
      </label>
      {children}
    </div>
  );
}

// A non-modal Popover does not dismiss on outside pointer presses (react-aria
// sets isDismissable to !isNonModal), so close it manually while keeping page
// scroll unlocked.
function CloseOnOutsidePress() {
  const state = useContext(OverlayTriggerStateContext);

  useEffect(() => {
    if (!state?.isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) {
        return;
      }
      if (target.closest(".events-date-range-picker__popover")) {
        return;
      }
      if (target.closest(".events-date-range-picker")) {
        return;
      }
      state.close();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [state]);

  return null;
}

function EventDateRangePicker({
  label,
  isRequired = false,
  startDate,
  endDate,
  minValue,
  onChange,
}: {
  label?: string;
  isRequired?: boolean;
  startDate: string;
  endDate: string;
  minValue?: DateValue;
  onChange: (nextStartDate: string, nextEndDate: string) => void;
}) {
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const value = useMemo(() => {
    if (!startDate || !endDate) {
      return null;
    }

    return {
      start: parseDate(startDate),
      end: parseDate(endDate),
    };
  }, [endDate, startDate]);

  const [draftValue, setDraftValue] = useState(value);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const formattedStartDate = startDate
    ? formatter.format(parseDate(startDate).toDate(getLocalTimeZone()))
    : "Select date";
  const formattedEndDate = endDate
    ? formatter.format(parseDate(endDate).toDate(getLocalTimeZone()))
    : "Select date";

  return (
    <AriaDateRangePicker
      aria-label={label ? undefined : "Event dates"}
      isRequired={isRequired}
      className="events-date-range-picker"
      minValue={minValue}
      shouldCloseOnSelect={false}
      value={draftValue}
      onChange={(nextValue) => {
        setDraftValue(nextValue);
      }}
    >
      {label ? (
        <AriaLabel className="events-legend-field__label">
          {label}
          {isRequired ? (
            <>
              <span aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </>
          ) : null}
        </AriaLabel>
      ) : null}
      <AriaGroup className="events-date-range-picker__group">
        <AriaButton className="events-date-range-picker__trigger">
          <div className="events-date-range-picker__segment">
            <span>Event Start</span>
            <strong>{formattedStartDate}</strong>
          </div>
          <div className="events-date-range-picker__divider" />
          <div className="events-date-range-picker__segment">
            <span>Event End</span>
            <strong>{formattedEndDate}</strong>
          </div>
          <span className="events-date-range-picker__icon" aria-hidden="true">
            <MaterialCalendarIcon className="size-6" />
          </span>
        </AriaButton>
      </AriaGroup>

      <AriaPopover
        isNonModal
        offset={10}
        placement="bottom"
        className={({ isEntering, isExiting }) =>
          cx(
            "fixed! top-1/2! bottom-auto! left-1/2! right-auto! z-50 flex max-h-[calc(100dvh-2rem)]! min-h-0 -translate-x-1/2! -translate-y-1/2! flex-col will-change-transform",
            isEntering && "duration-150 ease-out animate-in fade-in zoom-in-95",
            isExiting &&
              "duration-100 ease-in animate-out fade-out zoom-out-95",
          )
        }
      >
        <CloseOnOutsidePress />
        <AriaPopoverDialog className="events-date-range-picker__popover flex min-h-0 flex-col">
          {({ close }) => (
            <>
              <div className="events-date-range-picker__popover-header">
                <h4>Select Event Dates</h4>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                <RangeCalendar minValue={minValue} />
              </div>

              <div className="events-date-range-picker__popover-footer">
                <Button
                  size="md"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => {
                    setDraftValue(value);
                    close();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  color="primary"
                  className="events-solid-button"
                  onPress={() => {
                    if (draftValue?.start && draftValue?.end) {
                      onChange(
                        draftValue.start.toString(),
                        draftValue.end.toString(),
                      );
                    }
                    close();
                  }}
                >
                  Apply Dates
                </Button>
              </div>
            </>
          )}
        </AriaPopoverDialog>
      </AriaPopover>
    </AriaDateRangePicker>
  );
}

export function HomePage() {
  const { isAuthenticated } = useJwtAuth();
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  useQuery(GET_LIVE_CONTRACT, {
    variables: { code: EVENT_CONTRACT_CODE },
    skip: !isAuthenticated,
  });
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [pendingCreateEventValues, setPendingCreateEventValues] =
    useState<CreateEventFormValues | null>(null);
  const minimumEventDate = useMemo(() => getMinimumEventDate(), []);
  const defaultEventDate = minimumEventDate.toString();

  const savedValues = useMemo(() => loadPendingEvent(), []);

  const createEventForm = useValidatedForm(createEventSchema, {
    defaultValues: {
      eventName: savedValues?.eventName ?? "",
      eventType: savedValues?.eventType ?? "ONE_WORLD",
      organizerName: savedValues?.organizerName ?? "",
      organizerEmail: savedValues?.organizerEmail ?? "",
      venueName: savedValues?.venueName ?? "",
      venueAddress: savedValues?.venueAddress ?? "",
      description: savedValues?.description ?? "",
      destinationLabel: savedValues?.destinationLabel ?? "",
      primaryAirportCode: savedValues?.primaryAirportCode ?? "",
      departDate: savedValues?.departDate ?? defaultEventDate,
      returnDate: savedValues?.returnDate ?? "",
      passengers: savedValues?.passengers ?? "",
    },
  });
  const {
    clearEventNameAvailability,
    eventNameAvailabilityMessage,
    showEventNameChangedMessage,
    validateEventNameAvailability,
  } = useEventNameAvailability(createEventForm, organizationId);

  const primaryAirportCode = useWatch({
    control: createEventForm.control,
    name: "primaryAirportCode",
  });

  const navigateToAgreement = useCallback(
    (values: CreateEventFormValues) => {
      savePendingEvent(values);
      navigate("/review-agreement", { state: { formValues: values } });
    },
    [navigate],
  );

  const validateAndNavigateToAgreement = useCallback(
    async (values: CreateEventFormValues) => {
      const sanitizedValues = {
        ...values,
        eventName: sanitizeText(values.eventName),
      };
      const availabilityResult = await validateEventNameAvailability(
        sanitizedValues.eventName,
      );
      if (availabilityResult === "unavailable") {
        return;
      }
      if (availabilityResult === "stale") {
        showEventNameChangedMessage();
        return;
      }

      navigateToAgreement(sanitizedValues);
    },
    [
      navigateToAgreement,
      showEventNameChangedMessage,
      validateEventNameAvailability,
    ],
  );

  const handleCreateEvent = async (values: CreateEventFormValues) => {
    if (!isAuthenticated) {
      setPendingCreateEventValues(values);
      setIsAuthModalOpen(true);
      return;
    }

    await validateAndNavigateToAgreement(values);
  };

  const handleAuthSuccess = useCallback(async () => {
    setIsAuthModalOpen(false);
    if (pendingCreateEventValues) {
      const values = pendingCreateEventValues;
      setPendingCreateEventValues(null);
      await validateAndNavigateToAgreement(values);
    }
  }, [pendingCreateEventValues, validateAndNavigateToAgreement]);

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setIsAuthModalOpen(open);
    if (!open) {
      setPendingCreateEventValues(null);
    }
  }, []);

  return (
    <section className="events-page events-page--stack events-page--home">
      <section className="events-home-hero">
        <picture>
          <source srcSet={HOME_HERO_IMAGE_SRC} type="image/webp" />
          <img
            src={HOME_HERO_FALLBACK_IMAGE_SRC}
            alt="oneworld Meetings & Events destinations"
            width="1919"
            height="689"
            fetchPriority="high"
          />
        </picture>
      </section>

      <article className="events-home-form">
        <header className="events-home-form__header">
          <h1 className="events-page__title events-home-form__page-title">
            {formatOneworld("oneworld Meetings & Events")}
          </h1>
          <div>
            <h2 className="events-home-form__title">Submit your Interest.</h2>
          </div>
        </header>

        <ValidatedForm
          form={createEventForm}
          onSubmit={handleCreateEvent}
          className="events-home-form__body"
        >
          <div className="events-form-grid events-form-grid--four">
            <Controller
              control={createEventForm.control}
              name="eventName"
              render={({ field, fieldState }) => (
                <LegendField
                  label="Event Name"
                  controlId="event-name"
                  required
                  className="events-form-grid__span-2"
                >
                  <InputBase
                    {...field}
                    id="event-name"
                    aria-required="true"
                    size="md"
                    isInvalid={fieldState.invalid}
                    onBlur={() => {
                      field.onBlur();
                      if (isAuthenticated) {
                        void validateEventNameAvailability(field.value);
                      }
                    }}
                    onChange={(value) => {
                      clearEventNameAvailability();
                      field.onChange(value);
                    }}
                    placeholder="e.g. Annual Leadership Conference"
                    wrapperClassName="events-home-field-control"
                  />
                  {fieldState.error ? (
                    <span className="events-form-error">
                      {fieldState.error.message}
                    </span>
                  ) : eventNameAvailabilityMessage ? (
                    <span className="events-form-error" role="status">
                      {eventNameAvailabilityMessage}
                    </span>
                  ) : null}
                </LegendField>
              )}
            />

            <Controller
              control={createEventForm.control}
              name="venueName"
              render={({ field, fieldState }) => (
                <LegendField
                  label="Venue Name"
                  controlId="venue-name"
                  required
                  className="events-form-grid__span-2"
                >
                  <InputBase
                    {...field}
                    id="venue-name"
                    aria-required="true"
                    size="md"
                    isInvalid={fieldState.invalid}
                    placeholder="e.g. Grand Hyatt New York"
                    wrapperClassName="events-home-field-control"
                  />
                  {fieldState.error ? (
                    <span className="events-form-error">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </LegendField>
              )}
            />

            <Controller
              control={createEventForm.control}
              name="venueAddress"
              render={({ field, fieldState }) => (
                <LegendField
                  label="Venue Address"
                  controlId="venue-address"
                  required
                  className="events-form-grid__span-4"
                >
                  <PlacesAutocompleteInput
                    {...field}
                    ref={field.ref}
                    id="venue-address"
                    aria-required="true"
                    size="md"
                    isInvalid={fieldState.invalid}
                    placeholder="e.g. 109 E 42nd St, New York, NY 10017"
                    wrapperClassName="events-home-field-control"
                    onPlaceSelect={({ address }) => {
                      createEventForm.setValue("venueAddress", address, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                  {fieldState.error ? (
                    <span className="events-form-error">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </LegendField>
              )}
            />

            <Controller
              control={createEventForm.control}
              name="description"
              render={({ field, fieldState }) => {
                const trimmedDescriptionLength = field.value.trim().length;

                return (
                  <LegendField
                    label="Description"
                    controlId="event-description"
                    className="events-form-grid__span-4 events-home-form__description"
                  >
                    <TextAreaBase
                      id="event-description"
                      rows={4}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      placeholder="Add a brief description of your event for attendees."
                      className={
                        fieldState.invalid ? "ring-error_subtle" : undefined
                      }
                    />
                    <div className="events-home-form__description-meta">
                      {fieldState.error ? (
                        <span className="events-form-error">
                          {fieldState.error.message}
                        </span>
                      ) : (
                        <span />
                      )}
                      <span
                        className={cx(
                          "events-home-form__description-count",
                          trimmedDescriptionLength > DESCRIPTION_MAX_LENGTH &&
                            "events-form-error",
                        )}
                      >
                        {trimmedDescriptionLength}/{DESCRIPTION_MAX_LENGTH}
                      </span>
                    </div>
                  </LegendField>
                );
              }}
            />
          </div>

          <section className="events-home-form__group">
            <h3>Group Information</h3>

            <div className="events-form-grid events-form-grid--two">
              <p className="events-home-form__prompt">
                How many travelers are in your group?
              </p>

              <Controller
                control={createEventForm.control}
                name="passengers"
                render={({ field, fieldState }) => (
                  <LegendField
                    label="Number"
                    controlId="traveler-count"
                    required
                    className="events-home-form__traveler-field"
                  >
                    <InputBase
                      {...field}
                      id="traveler-count"
                      aria-required="true"
                      type="number"
                      inputMode="numeric"
                      onChange={(
                        value: string | ChangeEvent<HTMLInputElement>,
                      ) => {
                        const nextValue =
                          typeof value === "string"
                            ? value
                            : value.target.value;
                        field.onChange(nextValue.replace(/\D/g, ""));
                      }}
                      size="md"
                      isInvalid={fieldState.invalid}
                      placeholder="e.g. 250"
                      wrapperClassName="events-home-field-control"
                    />
                    {fieldState.error ? (
                      <span className="events-form-error" role="alert">
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </LegendField>
                )}
              />
            </div>
          </section>

          <section className="events-home-form__group">
            <h3 id="event-destination-label">
              Event Destination<span aria-hidden="true">*</span>
            </h3>

            <div className="events-event-info-row">
              <Controller
                control={createEventForm.control}
                name="destinationLabel"
                render={({ field, fieldState }) => (
                  <div>
                    <HeroComboBox
                      sublabel=""
                      options={destinationOptions}
                      showOptionsOnEmpty={false}
                      value={field.value}
                      selectedCode={primaryAirportCode}
                      onChange={(option) => {
                        createEventForm.setValue(
                          "destinationLabel",
                          option?.label ?? "",
                          {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          },
                        );
                        createEventForm.setValue(
                          "primaryAirportCode",
                          option?.code ?? "",
                          {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          },
                        );
                      }}
                      aria-label="Destination city"
                      aria-labelledby="event-destination-label"
                      isRequired
                      placeholder="Search"
                    />
                    {fieldState.error ? (
                      <span className="events-form-error">
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </div>
                )}
              />

              <div className="events-legend-field events-home-form__date-field">
                <Controller
                  control={createEventForm.control}
                  name="departDate"
                  render={({
                    field: departDateField,
                    fieldState: departDateFieldState,
                  }) => (
                    <Controller
                      control={createEventForm.control}
                      name="returnDate"
                      render={({
                        field: returnDateField,
                        fieldState: returnDateFieldState,
                      }) => (
                        <>
                          <EventDateRangePicker
                            label="Select event dates"
                            isRequired
                            startDate={departDateField.value}
                            endDate={returnDateField.value}
                            minValue={minimumEventDate}
                            onChange={(nextStartDate, nextEndDate) => {
                              departDateField.onChange(nextStartDate);
                              returnDateField.onChange(nextEndDate);
                            }}
                          />
                          {departDateFieldState.error ||
                          returnDateFieldState.error ? (
                            <span className="events-form-error">
                              {departDateFieldState.error?.message ??
                                returnDateFieldState.error?.message}
                            </span>
                          ) : null}
                        </>
                      )}
                    />
                  )}
                />
              </div>
            </div>
          </section>

          {/* Terms checkbox hidden — decision pending on whether to restore or fold into agreement */}

          <div className="events-home-form__actions">
            <Button
              size="xl"
              color="primary"
              className="events-create-button"
              type="submit"
              isDisabled={createEventForm.isSubmitting}
            >
              {createEventForm.isSubmitting ? "Checking..." : "Review & Sign"}
            </Button>
          </div>
        </ValidatedForm>
      </article>

      <AuthModal
        isOpen={isAuthModalOpen}
        onOpenChange={handleAuthModalOpenChange}
        onSuccess={handleAuthSuccess}
      />
    </section>
  );
}
